import React, { PureComponent } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import EndMeetingConfirmationContainer from '/imports/ui/components/end-meeting-confirmation/container';
import LeaveSessionConfirmationContainer from '/imports/ui/components/leave-session-confirmation/container';
import BBBMenu from '/imports/ui/components/common/menu/component';
import Styled from './styles';
import Session from '/imports/ui/services/storage/in-memory';
import Auth from '/imports/ui/services/auth';
import { exportWhiteboardData, logoutUser } from '../../app/service';
import ConfirmationModal from '/imports/ui/components/common/modal/confirmation/component';
import logger from '/imports/startup/client/logger';
import { captureWhiteboardState, getStateSummary } from '../../whiteboard/state-capture';

const intlMessages = defineMessages({
  leaveMeetingBtnLabel: {
    id: 'app.navBar.leaveMeetingBtnLabel',
    description: 'Leave meeting button label',
  },
  leaveMeetingBtnDesc: {
    id: 'app.navBar.leaveMeetingBtnDesc',
    description: 'Describes the leave meeting button',
  },
  leaveSessionLabel: {
    id: 'app.navBar.optionsDropdown.leaveSessionLabel',
    description: 'Leave session button label',
  },
  leaveSessionDesc: {
    id: 'app.navBar.optionsDropdown.leaveSessionDesc',
    description: 'Describes leave session option',
  },
  endMeetingLabel: {
    id: 'app.navBar.optionsDropdown.endMeetingForAllLabel',
    description: 'End meeting button label',
  },
  endMeetingDesc: {
    id: 'app.navBar.optionsDropdown.endMeetingDesc',
    description: 'Describes settings option closing the current meeting',
  },
  saveExitButtonLabel: {
    id: 'app.saveExit.buttonLabel',
    description: 'Save & Exit button label',
  },
  saveExitButtonDesc: {
    id: 'app.saveExit.buttonDesc',
    description: 'Save & Exit button description',
  },
  finishPreparationTitle: {
    id: 'app.saveExit.finishPreparationTitle',
    description: 'Finish classroom preparation modal title',
  },
  finishPreparationDescription: {
    id: 'app.saveExit.finishPreparationDescription',
    description: 'Finish classroom preparation modal description',
  },
  confirmButtonLabel: {
    id: 'app.saveExit.confirmButtonLabel',
    description: 'Confirm button label',
  },
  cancelButtonLabel: {
    id: 'app.saveExit.cancelButtonLabel',
    description: 'Cancel button label',
  },
});

const propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  amIModerator: PropTypes.bool,
  isBreakoutRoom: PropTypes.bool,
  connected: PropTypes.bool.isRequired,
  isDropdownOpen: PropTypes.bool,
  ismobile: PropTypes.bool.isRequired,
  userLeaveMeeting: PropTypes.func.isRequired,
  meetingEnd: PropTypes.func.isRequired,
  openLeaveMenu: PropTypes.string,
  courseDetails: PropTypes.any, // Or a more specific shape if known
};

const defaultProps = {
  amIModerator: false,
  isBreakoutRoom: false,
  isDropdownOpen: false
};

class LeaveMeetingButton extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isEndMeetingConfirmationModalOpen: false,
      isLeaveSessionConfirmationModalOpen: false,
      isSaveExitModalOpen: false,
    };



    this.setEndMeetingConfirmationModalIsOpen = this
      .setEndMeetingConfirmationModalIsOpen.bind(this);
    this.setLeaveSessionConfirmationModalIsOpen = this
      .setLeaveSessionConfirmationModalIsOpen.bind(this);
    this.setSaveExitModalIsOpen = this.setSaveExitModalIsOpen.bind(this);
    this.leaveSession = this.leaveSession.bind(this);
    this.handleSaveExit = this.handleSaveExit.bind(this);
  }

  setEndMeetingConfirmationModalIsOpen(value) {
    this.setState({ isEndMeetingConfirmationModalOpen: value });
  }

  setSaveExitModalIsOpen(value) {
    this.setState({ isSaveExitModalOpen: value });
  }

  endSession() {
    this.setEndMeetingConfirmationModalIsOpen(true);
  }

  setLeaveSessionConfirmationModalIsOpen(value) {
    this.setState({ isLeaveSessionConfirmationModalOpen: value });
  }

  leaveSession() {
    this.setLeaveSessionConfirmationModalIsOpen(true);
  }

  async handleSaveExit() {
    const { courseDetails, meetingEnd } = this.props;

    logger.info({
      logCode: 'save_exit_button_clicked',
      extraInfo: { logType: 'user_action' },
    }, 'Save & Exit button clicked, finishing classroom preparation');

    // Capture whiteboard state before saving
    const editor = window.tlEditorRef?.current;
    // Get current slide from tldraw editor's current page
    const currentSlide = editor?.getCurrentPageId ?
      parseInt(editor.getCurrentPageId().replace('page:', ''), 10) || 1 : 1;

    const whiteboardState = captureWhiteboardState(
      editor,
      courseDetails?.course?.courseDetail?.presentationId,
      currentSlide
    );

    logger.info({
      logCode: 'whiteboard_export_save_exit',
      extraInfo: { summary: getStateSummary(whiteboardState) },
    }, 'Exporting whiteboard state on save & exit');

    const meetingId = Auth?._meetingID;
    const hasImportedKey = `hasImported-${meetingId}`;

    // CRITICAL: Only export if we have valid whiteboard state with content
    // This prevents overwriting existing annotations with an empty state
    const hasValidState = whiteboardState && whiteboardState.slides && whiteboardState.slides.length > 0;

    try {
      if (hasValidState) {
        const exportApiPayload = {
          internalMeetingID: Auth?._meetingID,
          externalUserID: Auth?._externUserID,
          sessionId: courseDetails?.course?.courseDetail?.courseSessionId,
          state: whiteboardState,
        };

        // Await the export to ensure data is saved before ending meeting
        await exportWhiteboardData(exportApiPayload);
        logger.info({
          logCode: 'whiteboard_export_success',
        }, 'Save & Exit whiteboard export completed successfully');
      } else {
        logger.warn({
          logCode: 'whiteboard_export_skipped_empty',
          extraInfo: { hasState: !!whiteboardState, hasSlides: !!whiteboardState?.slides },
        }, 'Skipping whiteboard export - no valid state to save (preserving existing annotations)');
      }

      this.setState({ isSaveExitModalOpen: false });

      logger.warn({
        logCode: 'moderator_forcing_end_meeting',
        extraInfo: { logType: 'user_action' },
      }, 'Save & Exit: finishing classroom preparation and ending meeting');

      localStorage.removeItem(hasImportedKey);

      if (!Auth?._meetingID && !Auth?._externUserID) return;

      const apiPayload = {
        internalMeetingID: Auth?._meetingID,
        externalUserID: Auth?._externUserID,
        courseSessionId: courseDetails?.course?.courseDetail?.courseSessionId,
        isPrepareLesson: courseDetails?.course?.courseDetail?.isPrepareLesson
      };

      try {
        await logoutUser(apiPayload);
      } catch (error) {
        logger.error({
          logCode: 'logout_user_failed',
          extraInfo: { error: error?.message },
        }, 'Logout user failed during save & exit');
      } finally {
        meetingEnd();
        Session.setItem('codeError', this.LOGOUT_CODE);
      }

      logger.info({
        logCode: 'save_exit_successful',
        extraInfo: { logType: 'user_action' },
      }, 'Save & Exit completed successfully');
    } catch (error) {
      logger.error({
        logCode: 'save_exit_failed',
        extraInfo: { error: error?.message },
      }, 'Save & Exit failed');
    }
  }

  renderMenuItems() {
    const {
      intl, amIModerator, isBreakoutRoom, connected,
    } = this.props;

    const allowedToEndMeeting = amIModerator && !isBreakoutRoom;

    const { allowLogout: allowLogoutSetting } = window.meetingClientSettings.public.app;

    this.menuItems = [];

    if (allowLogoutSetting && connected) {
      this.menuItems.push(
        {
          key: 'list-item-logout',
          dataTest: 'directLogoutButton',
          icon: 'logout',
          label: intl.formatMessage(intlMessages.leaveSessionLabel),
          description: intl.formatMessage(intlMessages.leaveSessionDesc),
          onClick: () => this.leaveSession(),
        },
      );
    }

    if (allowedToEndMeeting && connected) {
      this.menuItems.push(
        {
          key: 'list-item-end-meeting',
          dataTest: 'endMeetingButton',
          icon: 'close',
          label: intl.formatMessage(intlMessages.endMeetingLabel),
          description: intl.formatMessage(intlMessages.endMeetingDesc),
          // onClick: () => this.setEndMeetingConfirmationModalIsOpen(true),
          onClick: () => this.endSession()
        },
      );
    }

    return this.menuItems;
  }

  // eslint-disable-next-line class-methods-use-this
  renderModal(
    isOpen,
    setIsOpen,
    priority,
    Component,
    otherOptions,
  ) {

    const { courseDetails } = this.props
    return isOpen ? (
      <Component
        {...{
          ...otherOptions,
          onRequestClose: () => setIsOpen(false),
          priority,
          setIsOpen,
          isOpen,
          courseDetails
        }}
      />
    ) : null;
  }

  render() {
    const {
      intl,
      isDropdownOpen,
      ismobile,
      isRTL,
      openLeaveMenu,
      courseDetails,
      amIModerator,
    } = this.props;

    const { isEndMeetingConfirmationModalOpen, isLeaveSessionConfirmationModalOpen, isSaveExitModalOpen } = this.state;

    // Check if we should show Save & Exit button instead of leave dropdown
    const isPrepareLesson = courseDetails?.course?.courseDetail?.isPrepareLesson ?? false;
    const shouldShowSaveExit = amIModerator && isPrepareLesson;

    const customStyles = { top: '1rem' };

    // If in prepare lesson mode, show Save & Exit button
    if (shouldShowSaveExit) {
      return (
        <>
          <Styled.SaveExitButton
            onClick={() => this.setSaveExitModalIsOpen(true)}
            data-test="saveExitButton"
            aria-label={'saveExitButton'}
            title={'Save & Exit'}
          >
            <Styled.ButtonContent>
              <Styled.ButtonText>
                Save & Exit
                {/* {intl.formatMessage(intlMessages.saveExitButtonLabel)} */}
              </Styled.ButtonText>
            </Styled.ButtonContent>
          </Styled.SaveExitButton>

          {isSaveExitModalOpen && (
            <ConfirmationModal
              intl={intl}
              onConfirm={this.handleSaveExit}
              title={'Finish Classroom Preparation'}
              description={'Are you sure you want to finish preparing the classroom'}
              confirmButtonLabel={'Yes'}
              cancelButtonLabel={'No'}
              confirmButtonColor="primary"
              confirmButtonDataTest="confirmSaveExit"
              isOpen={isSaveExitModalOpen}
              onRequestClose={() => this.setSaveExitModalIsOpen(false)}
              priority="high"
              setIsOpen={this.setSaveExitModalIsOpen}
            />
          )}

          {this.renderModal(isEndMeetingConfirmationModalOpen,
            this.setEndMeetingConfirmationModalIsOpen,
            'low', EndMeetingConfirmationContainer)}
        </>
      );
    }

    // Otherwise show the regular leave dropdown
    return (
      <>
        <BBBMenu
          customStyles={!ismobile ? customStyles : null}
          trigger={(
            <Styled.LeaveButton
              state={isDropdownOpen ? 'open' : 'closed'}
              ismobile={ismobile.toString()}
              accessKey={openLeaveMenu}
              aria-label={intl.formatMessage(intlMessages.leaveMeetingBtnLabel)}
              label={intl.formatMessage(intlMessages.leaveMeetingBtnLabel)}
              tooltipLabel={intl.formatMessage(intlMessages.leaveMeetingBtnLabel)}
              description={intl.formatMessage(intlMessages.leaveMeetingBtnDesc)}
              data-test="leaveMeetingDropdown"
              icon="logout"
              color="danger"
              size="lg"
              hideLabel
              label="Leave"
              // FIXME: Without onClick react proptypes keep warning
              // even after the DropdownTrigger inject an onClick handler
              onClick={() => null}
            />
          )}
          actions={this.renderMenuItems()}
          opts={{
            id: 'app-leave-meeting-menu',
            keepMounted: true,
            transitionDuration: 0,
            elevation: 3,
            getcontentanchorel: null,
            fullwidth: 'true',
            anchorOrigin: { vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' },
            transformorigin: { vertical: 'top', horizontal: isRTL ? 'left' : 'right' },
          }}
        />
        {this.renderModal(isEndMeetingConfirmationModalOpen,
          this.setEndMeetingConfirmationModalIsOpen,
          'low', EndMeetingConfirmationContainer)}
        {this.renderModal(isLeaveSessionConfirmationModalOpen,
          this.setLeaveSessionConfirmationModalIsOpen,
          'low', LeaveSessionConfirmationContainer)}
      </>
    );
  }
}

LeaveMeetingButton.propTypes = propTypes;
LeaveMeetingButton.defaultProps = defaultProps;
export default injectIntl(LeaveMeetingButton);
